import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SheetOrModal from '@/components/ui/modal/SheetOrModal';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { buildApiUrl } from '@/config/api';
import { makeBenefits } from './planBenefits';
import { promoThenPrice } from './promoThenPrice';
const upgradeTitleIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 448 512"
    className="h-5 w-5 shrink-0"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M338.8-9.9c11.9 8.6 16.3 24.2 10.9 37.8L271.3 224 416 224c13.5 0 25.5 8.4 30.1 21.1s.7 26.9-9.6 35.5l-288 240c-11.3 9.4-27.4 9.9-39.3 1.3s-16.3-24.2-10.9-37.8L176.7 288 32 288c-13.5 0-25.5-8.4-30.1-21.1s-.7-26.9 9.6-35.5l288-240c11.3-9.4 27.4-9.9 39.3-1.3z" />
  </svg>
);

const DEFAULT_PLAN = { name: 'starter', label: 'Starter', price: 29, studentLimit: 10, videoRetentionKey: 'video_retention_1_month' };

/**
 * Format a Stripe minor-unit amount (cents) as a euro string.
 */
const formatAmount = (amount, currency = 'eur') =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency.toUpperCase() })
    .format(Math.abs(amount) / 100);

/**
 * `proration` switches this modal from the first-subscription flow (Checkout, where an
 * affiliation code can still be entered) to the in-place plan change flow, where the
 * coach already has a subscription and we show what they will actually be charged now.
 * Shape: { amountDue, currency, isImmediateCharge }
 */
const UpgradeConfirmationModal = ({ isOpen, onClose, onConfirm, isLoading, plan, proration = null, discountPercent = null, discountMonths = null }) => {
  const { t } = useTranslation('dashboard');
  const { getAuthToken } = useAuth();
  const resolvedPlan = plan || DEFAULT_PLAN;
  const benefits = makeBenefits(t, resolvedPlan.studentLimit, resolvedPlan.videoRetentionKey);
  const isPlanChange = proration !== null;

  const [affiliationCode, setAffiliationCode] = useState('');
  const [codeValidation, setCodeValidation] = useState(null);
  const [codeChecking, setCodeChecking] = useState(false);

  const validateCode = useCallback(async (code) => {
    if (!code.trim()) { setCodeValidation(null); return; }
    setCodeChecking(true);
    try {
      const token = await getAuthToken();
      const res = await fetch(buildApiUrl(`/api/billing/validate-affiliation-code?code=${encodeURIComponent(code)}`), {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      setCodeValidation(data.data);
    } catch {
      setCodeValidation({ valid: false, reason: 'error' });
    } finally {
      setCodeChecking(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    const timer = setTimeout(() => validateCode(affiliationCode), 600);
    return () => clearTimeout(timer);
  }, [affiliationCode, validateCode]);

  useEffect(() => {
    if (!isOpen) {
      setAffiliationCode('');
      setCodeValidation(null);
    }
  }, [isOpen]);

  return (
    <SheetOrModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Passer à ${resolvedPlan.label}`}
      titleIcon={upgradeTitleIcon}
      modalId="upgrade-confirmation"
      size="sm"
      // Above the plans modal (default 50): this confirmation is opened from inside it,
      // and relying on portal insertion order alone is fragile.
      zIndex={60}
      sheetZ="z-[100]"
      className="!min-w-0 w-full !max-w-[min(25rem,calc(100vw-2rem))]"
    >
      <div className="flex flex-col gap-6">
        {/* Price */}
        <div className="text-center mt-1.5 mb-1.5">
          {discountPercent ? (
            /* A promotion code is applied — quoting the list price here would
               contradict the plans screen the coach just came from. */
            <>
              <span className="text-lg font-light text-white/35 line-through mr-2">€{resolvedPlan.price}</span>
              <span className="text-4xl font-bold text-[#4ade80]">
                €{(Math.round(resolvedPlan.price * (1 - discountPercent / 100) * 100) / 100).toFixed(2).replace('.00', '')}
              </span>
              <span className="text-lg text-zinc-400 font-light">/mois</span>
              <p className="mt-1 text-xs text-[#4ade80]">Code promo appliqué : −{discountPercent}%</p>
              {discountMonths ? (
                <p className="mt-0.5 text-xs font-light text-white/50">{promoThenPrice(t, discountMonths, resolvedPlan.price)}</p>
              ) : null}
            </>
          ) : (
            <>
              <span className="text-4xl font-bold text-white">€{resolvedPlan.price}</span>
              <span className="text-lg text-zinc-400 font-light">/mois</span>
            </>
          )}
        </div>

        {/* Benefits */}
        <ul className="space-y-3 mt-1.5 mb-1.5">
          {benefits.map((benefit) => (
            <li key={benefit.text} className="flex items-center gap-3 text-sm text-zinc-300">
              {benefit.customIcon ? (
                <span className="inline-flex w-8 shrink-0 items-center justify-center">{benefit.customIcon}</span>
              ) : null}
              {benefit.text}
            </li>
          ))}
        </ul>

        {/* Prorated amount — in-place plan change only */}
        {isPlanChange && (
          <div className="rounded-[10px] border border-white/10 bg-white/5 px-4 py-3 text-left">
            {proration.amountDue > 0 ? (
              <>
                <p className="text-sm text-white">
                  À payer maintenant :{' '}
                  <span className="font-semibold">{formatAmount(proration.amountDue, proration.currency)}</span>
                </p>
                <p className="text-xs text-zinc-400 mt-1">
                  Montant calculé au prorata des jours restants. Votre prochaine facture repartira sur
                  le tarif normal de {resolvedPlan.label}.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-white">Rien à payer aujourd&apos;hui</p>
                <p className="text-xs text-zinc-400 mt-1">
                  {proration.amountDue < 0
                    ? `Un crédit de ${formatAmount(proration.amountDue, proration.currency)} sera déduit de votre prochaine facture.`
                    : 'Le changement prend effet immédiatement.'}
                </p>
              </>
            )}
          </div>
        )}

        {/* Affiliation code — only on first subscription; an existing subscriber
            applies a code from the billing page instead. */}
        {!isPlanChange && (
        <div className="space-y-2 text-left">
          <label className="text-sm font-medium text-zinc-300">Code d&apos;affiliation (optionnel)</label>
          <input
            type="text"
            value={affiliationCode}
            onChange={e => setAffiliationCode(e.target.value.toUpperCase())}
            placeholder="ex: MARC-47"
            className="w-full border border-white/10 rounded-lg px-3 py-2 text-sm font-mono uppercase bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
            maxLength={15}
          />
          {codeChecking && <p className="text-xs text-zinc-400">Vérification...</p>}
          {codeValidation?.valid && (
            <p className="text-xs text-green-500">{codeValidation.discount}</p>
          )}
          {codeValidation && !codeValidation.valid && (
            <p className="text-xs text-red-400">
              {codeValidation.reason === 'own_code' ? 'Vous ne pouvez pas utiliser votre propre code.'
                : codeValidation.reason === 'already_used' ? "Vous avez déjà utilisé un code d'affiliation."
                : 'Code invalide.'}
            </p>
          )}
        </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3 pt-2">
          <button
            onClick={() => onConfirm(codeValidation?.valid ? affiliationCode : null)}
            disabled={isLoading}
            className="w-full py-3 rounded-[8px] text-sm font-semibold text-white flex items-center justify-center gap-2 bg-gradient-to-r from-kaiylo-secondary to-kaiylo-secondary2 transition-[transform,box-shadow,filter] duration-300 ease-out hover:brightness-[1.04] hover:shadow-[0_8px_26px_-12px_rgba(168,85,247,0.28)] hover:-translate-y-px active:translate-y-0 active:brightness-[1.02] active:shadow-[0_4px_18px_-10px_rgba(15,102,201,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/45 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 disabled:pointer-events-none disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Chargement...
              </>
            ) : (
              isPlanChange ? 'Confirmer le changement' : 'Continuer'
            )}
          </button>
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-lg text-sm font-medium text-zinc-400 transition-all duration-300 hover:text-white hover:bg-white/5"
          >
            Annuler
          </button>
        </div>
      </div>
    </SheetOrModal>
  );
};

export default UpgradeConfirmationModal;
